/**
 * Authentication Service
 *
 * Handles all authentication-related API calls: login, registration,
 * logout, fetching the current user profile, and token refresh.
 *
 * All methods return an ApiResponse wrapper. The caller (typically an
 * auth context or store) is responsible for persisting the token to
 * localStorage and managing auth state.
 *
 * Backend endpoints: POST /api/auth/login, POST /api/auth/register,
 * POST /api/auth/logout, GET /api/auth/me, POST /api/auth/refresh
 */

import { api } from './api';
import type { UserRole } from '../utils/constants';

/**
 * Represents an authenticated user in the system.
 *
 * @property id - Unique user identifier (UUID from backend)
 * @property email - User's email address; used for login
 * @property firstName - User's first name for display purposes
 * @property lastName - User's last name for display purposes
 * @property role - The user's role which determines their permissions (admin, manager, technician, reviewer)
 * @property companyId - The company this user belongs to; scopes data access
 * @property canViewFinancials - Whether this user can see billing/pricing information
 * @property canReassign - Whether this user can reassign work orders to other technicians
 * @property profilePhotoUrl - URL to the user's avatar image, or null if not set
 * @property authProvider - How the user authenticates: email/password, Office 365 SSO, or Google SSO
 */
export interface User {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	/** Array of all role slugs assigned to this user via the user_roles junction table */
	roles: string[];
	/** Primary/highest-privilege role for backward compatibility — derived from roles array */
	role: UserRole;
	phone: string | null;
	companyId: string;
	/** Array of all company IDs this user is associated with via the company_users junction table */
	companyIds: string[];
	canViewFinancials: boolean;
	canReassign: boolean;
	profilePhotoUrl: string | null;
	/** 50x50 thumbnail version of the profile photo for nav/sidebar use */
	profilePhotoThumbUrl: string | null;
	authProvider: 'email' | 'o365' | 'google';
	/** When true, the user must change their password before accessing the app.
	 *	Set on first login (admin-created users) or after an admin password reset. */
	mustChangePassword: boolean;
	/** IANA timezone identifier for the user's local time (e.g. "America/New_York").
	 *	Used to convert UTC times from the API into the user's local time for display
	 *	and to interpret user-entered scheduling times. Defaults to Eastern Time. */
	timezone: string;
}

/**
 * Payload for the login request.
 *
 * @property email - The user's email address
 * @property password - The user's plaintext password (sent over HTTPS)
 */
export interface LoginCredentials {
	email: string;
	password: string;
	/** When false, the backend issues a 2-hour token; when true/omitted, no expiry */
	remember_me?: boolean;
}

/**
 * Payload for the registration request.
 *
 * @property email - Desired email address for the new account
 * @property password - Desired password (must pass validatePassword rules)
 * @property firstName - User's first name
 * @property lastName - User's last name
 * @property companyId - Optional company to associate with; if omitted, a new company may be created
 */
export interface RegisterData {
	email: string;
	password: string;
	firstName: string;
	lastName: string;
	companyId?: string;
}

/**
 * Shape of the response from login and register endpoints.
 * The backend returns a flat token string (Sanctum personal access token),
 * not a nested tokens object. The token is used as a Bearer token in
 * subsequent API requests via the Authorization header.
 */
interface LoginResponse {
	user: Record<string, any>;
	token: string;
	/** Whether the user opted into "Remember Me" */
	remember_me: boolean;
	/** ISO 8601 expiry timestamp, or null if the token has no expiry */
	expires_at: string | null;
}

/**
 * Shape of the response from the impersonate endpoint.
 * Includes the target user, a new token for the impersonated session,
 * and the original admin's identity so the frontend can show a banner
 * and revert the impersonation.
 */
interface ImpersonateResponse {
	user: Record<string, any>;
	token: string;
	original_user: { id: number; name: string };
	is_impersonating: boolean;
}

/**
 * Shape of the response from the stop-impersonation endpoint.
 * Returns the original admin's user data and their token to restore
 * the real admin session.
 */
interface StopImpersonationResponse {
	user: Record<string, any>;
	token: string;
	is_impersonating: boolean;
}

/**
 * Authentication service methods.
 * Each method maps to a specific backend API endpoint.
 */
export const authService = {
	/** POST /api/auth/login — Authenticate with email and password, returns user + tokens */
	login: (credentials: LoginCredentials) =>
		api.post<LoginResponse>('/auth/login', credentials),

	/** POST /api/auth/register — Create a new user account, returns user + tokens */
	register: (data: RegisterData) =>
		api.post<LoginResponse>('/auth/register', data),

	/** POST /api/auth/logout — Revoke the current Sanctum token (requires auth) */
	logout: () => api.post<void>('/auth/logout'),

	/** GET /api/auth/me — Fetch the currently authenticated user's profile (requires auth) */
	getCurrentUser: () => api.get<User>('/auth/me'),

	/** POST /api/auth/refresh — Exchange a refresh token for a new access token (not yet implemented) */
	refreshToken: (refreshToken: string) =>
		api.post<{ token: string }>('/auth/refresh', { refreshToken }),

	/** POST /api/auth/impersonate/{userId} — Start impersonating a target user (super_admin only) */
	impersonate: (userId: string) =>
		api.post<ImpersonateResponse>(`/auth/impersonate/${userId}`),

	/** POST /api/auth/stop-impersonation — Stop impersonating and revert to original admin session */
	stopImpersonation: (originalToken: string) =>
		api.post<StopImpersonationResponse>('/auth/stop-impersonation', { original_token: originalToken }),
};
